import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Trash2, PackageCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/lib/settings";
import { todayISO } from "@/lib/period";
import { PageHeader, Empty } from "@/components/Bits";
import StatusChip from "@/components/StatusChip";
import Modal from "@/components/Modal";

export function PayVendorModal({ po, onClose, onDone }) {
  const s = useSettings();
  const [f, setF] = useState({ amount: "", method: "Bank", date: todayISO() });
  useEffect(() => {
    if (po) setF({ amount: (po.payable > 0 ? po.payable : po.total - po.amount_paid).toFixed(2), method: s?.payment_methods?.includes("Bank") ? "Bank" : s?.payment_methods?.[0] || "Cash", date: todayISO() });
  }, [po, s]);
  if (!po) return null;
  const submit = async () => {
    try {
      await api.post(`/purchase-orders/${po.id}/pay`, { amount: Number(f.amount), method: f.method, date: f.date });
      toast.success("Vendor payment recorded");
      onDone?.(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal open={!!po} onClose={onClose} title={`Pay vendor · ${po.po_number}`} testid="pay-vendor-modal">
      <div className="text-sm mb-4 space-y-1">
        <div className="flex justify-between"><span className="text-[#5C626A]">{po.vendor_name || po.vendor?.name}</span><span>Order total <b className="font-num">{inr(po.total)}</b></span></div>
        <div className="flex justify-between"><span className="text-[#5C626A]">Paid so far <span className="font-num">{inr(po.amount_paid)}</span></span><span>Due for goods received <b className="font-num text-[#A3411D]">{inr(po.payable)}</b></span></div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div><label className="label">Amount</label><input type="number" className="field font-num" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="pay-vendor-amount" /></div>
        <div><label className="label">Method</label><select className="field" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}>{(s?.payment_methods || ["Cash", "Bank"]).map((m) => <option key={m}>{m}</option>)}</select></div>
        <div><label className="label">Date</label><input type="date" className="field" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
      </div>
      <div className="flex justify-end gap-2"><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={!(Number(f.amount) > 0)} data-testid="pay-vendor-submit">Record payment</button></div>
    </Modal>
  );
}

function CreatePO({ open, onClose, vendors, onDone }) {
  const s = useSettings();
  const [vendorId, setVendorId] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [lines, setLines] = useState([]);
  const [freight, setFreight] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [receiveNow, setReceiveNow] = useState(false);
  const [billNo, setBillNo] = useState("");
  const [orderDate, setOrderDate] = useState(todayISO());

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => setResults((await api.get(`/skus/search?q=${encodeURIComponent(q)}`)).data), 200);
    return () => clearTimeout(t);
  }, [q]);

  const addLine = (s) => {
    if (lines.find((l) => l.sku_id === s.id)) return;
    setLines((l) => [...l, { sku_id: s.id, label: `${s.style_name} · ${s.color}/${s.size}`, qty_ordered: 6, unit_cost: s.cost_price || 0, tax_rate: s.tax_rate ?? 5 }]);
    setQ(""); setResults([]);
  };
  const upd = (id, patch) => setLines((l) => l.map((x) => x.sku_id === id ? { ...x, ...patch } : x));

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    lines.forEach((l) => { const lt = l.qty_ordered * l.unit_cost; sub += lt; tax += lt * l.tax_rate / 100; });
    return { sub, tax, total: sub + tax + Number(freight) - Number(discount) };
  }, [lines, freight, discount]);

  const submit = async () => {
    try {
      await api.post("/purchase-orders", {
        vendor_id: vendorId, freight: Number(freight), discount: Number(discount),
        order_date: orderDate, vendor_bill_no: billNo, receive_now: receiveNow,
        lines: lines.map((l) => ({ sku_id: l.sku_id, qty_ordered: Number(l.qty_ordered), unit_cost: Number(l.unit_cost), tax_rate: Number(l.tax_rate) })),
      });
      toast.success(receiveNow ? "Purchase recorded · stock added" : "Purchase order created");
      onDone(); onClose();
      setVendorId(""); setLines([]); setFreight(0); setDiscount(0); setReceiveNow(false); setBillNo(""); setOrderDate(todayISO());
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal open={open} onClose={onClose} title={receiveNow ? "Record Purchase (goods received)" : "New Purchase Order"} wide testid="create-po">
      <div className="flex gap-1 bg-[#F4F0EA] rounded-full p-1 mb-4 w-fit">
        {[[false, "Order from vendor"], [true, "Goods already received"]].map(([v, lbl]) => (
          <button key={lbl} onClick={() => setReceiveNow(v)} data-testid={`po-mode-${v ? "received" : "order"}`}
            className={`px-3.5 py-1.5 rounded-full text-sm ${receiveNow === v ? "bg-[#0E1B2E] text-white" : "text-[#5C626A]"}`}>{lbl}</button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <label className="label">Vendor</label>
          <select className="field" value={vendorId} onChange={(e) => setVendorId(e.target.value)} data-testid="po-vendor">
            <option value="">Select vendor…</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </div>
        <div><label className="label">{receiveNow ? "Bill date" : "Order date"}</label><input type="date" className="field" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} /></div>
        <div><label className="label">Vendor bill no.</label><input className="field font-num" value={billNo} onChange={(e) => setBillNo(e.target.value)} placeholder={receiveNow ? "From the vendor's invoice" : "Optional"} data-testid="po-bill-no" /></div>
      </div>
      {vendors.length === 0 && <div className="text-sm text-[#A3411D] mb-3">Add a vendor first (Vendors page).</div>}

      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C929B]" />
        <input className="field pl-9" placeholder="Search SKUs to add…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="po-sku-search" />
        {results.length > 0 && (
          <div className="absolute top-11 left-0 right-0 card shadow-xl z-40 max-h-64 overflow-auto">
            {results.map((s) => (
              <button key={s.id} onClick={() => addLine(s)} className="w-full text-left px-4 py-2 hover:bg-[#F4F0EA] text-sm flex justify-between" data-testid={`po-add-${s.id}`}>
                <span>{s.style_name} · {s.color}/{s.size}</span><span className="font-num text-[#8C929B]">{s.style_code}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border border-[#E6E1D8] rounded-lg overflow-hidden mb-4">
        <table className="tbl">
          <thead><tr><th>Item</th><th className="num">Qty</th><th className="num">Unit cost</th><th className="num">Tax %</th><th className="num">Line total</th><th></th></tr></thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.sku_id}>
                <td>{l.label}</td>
                <td className="num"><input type="number" className="field py-1 px-2 w-16 text-right font-num" value={l.qty_ordered} onChange={(e) => upd(l.sku_id, { qty_ordered: e.target.value })} /></td>
                <td className="num"><input type="number" className="field py-1 px-2 w-24 text-right font-num" value={l.unit_cost} onChange={(e) => upd(l.sku_id, { unit_cost: e.target.value })} /></td>
                <td className="num">
                  <select className="field py-1 px-2 w-20 text-right font-num" value={l.tax_rate} onChange={(e) => upd(l.sku_id, { tax_rate: e.target.value })}>
                    {[...new Set([0, ...(s?.tax_rates || [5, 12, 18]), Number(l.tax_rate)])].sort((a, b) => a - b).map((r) => <option key={r} value={r}>{r}%</option>)}
                  </select>
                </td>
                <td className="num font-num">{inr(l.qty_ordered * l.unit_cost)}</td>
                <td className="num"><button onClick={() => setLines((x) => x.filter((y) => y.sku_id !== l.sku_id))} className="text-[#A3411D]"><Trash2 size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {lines.length === 0 && <div className="py-8 text-center text-sm text-[#8C929B]">Add SKUs to this order</div>}
      </div>

      <div className="flex justify-end gap-8 items-end">
        <div className="flex gap-3">
          <div><label className="label">Freight</label><input type="number" className="field py-1.5 w-28 font-num" value={freight} onChange={(e) => setFreight(e.target.value)} /></div>
          <div><label className="label">Discount</label><input type="number" className="field py-1.5 w-28 font-num" value={discount} onChange={(e) => setDiscount(e.target.value)} /></div>
        </div>
        <div className="text-right">
          <div className="text-sm text-[#5C626A]">Subtotal <span className="font-num ml-2">{inr(totals.sub)}</span></div>
          <div className="text-sm text-[#5C626A]">Tax <span className="font-num ml-2">{inr(totals.tax)}</span></div>
          <div className="text-lg font-num font-semibold mt-1">{inr(totals.total)}</div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={!vendorId || !lines.length} data-testid="po-submit">{receiveNow ? "Record purchase & add stock" : "Create order"}</button>
      </div>
    </Modal>
  );
}

function ReceivePO({ po, onClose, onDone }) {
  const [recv, setRecv] = useState({});
  useEffect(() => {
    if (po) {
      const init = {};
      po.lines.forEach((l) => { init[l.sku_id] = l.qty_ordered - l.qty_received; });
      setRecv(init);
    }
  }, [po]);
  if (!po) return null;
  const submit = async () => {
    try {
      await api.post(`/purchase-orders/${po.id}/receive`, {
        lines: Object.entries(recv).map(([sku_id, qty_received]) => ({ sku_id, qty_received: Number(qty_received) })),
      });
      toast.success("Stock received · weighted-average cost updated");
      onDone(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Modal open={!!po} onClose={onClose} title={`Receive · ${po.po_number}`} wide testid="receive-po">
      <div className="border border-[#E6E1D8] rounded-lg overflow-hidden mb-5">
        <table className="tbl">
          <thead><tr><th>Item</th><th className="num">Ordered</th><th className="num">Already recv</th><th className="num">Receive now</th></tr></thead>
          <tbody>
            {po.lines.map((l) => {
              const pending = l.qty_ordered - l.qty_received;
              return (
                <tr key={l.sku_id}>
                  <td>{l.style_name} · {l.color}/{l.size}</td>
                  <td className="num font-num">{l.qty_ordered}</td>
                  <td className="num font-num">{l.qty_received}</td>
                  <td className="num"><input type="number" min="0" max={pending} className="field py-1 px-2 w-20 text-right font-num" value={recv[l.sku_id] ?? 0} onChange={(e) => setRecv({ ...recv, [l.sku_id]: Math.max(0, Math.min(pending, parseInt(e.target.value || "0", 10))) })} data-testid={`recv-${l.sku_id}`} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end gap-2"><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} data-testid="receive-submit"><PackageCheck size={16} /> Confirm receipt</button></div>
    </Modal>
  );
}

export default function Purchases() {
  const { isOwner } = useAuth();
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [receiving, setReceiving] = useState(null);
  const [detail, setDetail] = useState(null);
  const [paying, setPaying] = useState(null);
  const [params, setParams] = useSearchParams();

  const load = async () => {
    setPos((await api.get("/purchase-orders")).data);
    setVendors((await api.get("/vendors")).data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get("new") && isOwner) setShowCreate(true);
    const id = params.get("po");
    if (id) api.get(`/purchase-orders/${id}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [params, isOwner]);

  const openReceive = async (po) => setReceiving((await api.get(`/purchase-orders/${po.id}`)).data);
  const openDetail = async (po) => setDetail((await api.get(`/purchase-orders/${po.id}`)).data);
  const cancelPO = async (po) => {
    if (!window.confirm(`Cancel ${po.po_number}?`)) return;
    try { await api.post(`/purchase-orders/${po.id}/cancel`); toast.success("Order cancelled"); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const totalPayable = pos.reduce((a, p) => a + (p.payable || 0), 0);

  return (
    <div>
      <PageHeader eyebrow="Wholesale" title="Purchases" subtitle="Order stock from vendors or record goods already received. Payables count only what has actually arrived.">
        {isOwner && <div className="text-right mr-2"><div className="font-num text-sm text-[#A3411D]">{inr(totalPayable)}</div><div className="text-xs text-[#8C929B]">payable to vendors</div></div>}
        {isOwner && <button className="btn btn-primary" onClick={() => setShowCreate(true)} data-testid="new-po-btn"><Plus size={16} /> New purchase</button>}
      </PageHeader>

      <div className="card overflow-hidden">
        <table className="tbl">
          <thead><tr><th>PO Number</th><th>Vendor</th><th>Date</th><th>Bill no.</th><th className="num">Order total</th><th className="num">Received value</th><th className="num">Payable</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {pos.map((p) => (
              <tr key={p.id} className="cursor-pointer" onClick={() => openDetail(p)} data-testid={`po-row-${p.po_number}`}>
                <td className="font-num">{p.po_number}</td>
                <td>{p.vendor_name}</td>
                <td className="text-[#5C626A]">{fmtDate(p.order_date)}</td>
                <td className="font-num text-xs text-[#8C929B]">{p.vendor_bill_no || "—"}</td>
                <td className="num font-num">{inr(p.total)}</td>
                <td className="num font-num text-[#5C626A]">{inr(p.billed)}</td>
                <td className="num font-num" style={{ color: p.payable > 0 ? "#A3411D" : "#8C929B" }}>{inr(p.payable)}</td>
                <td><StatusChip status={p.status} /></td>
                <td className="num" onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1 justify-end">
                    {["Ordered", "Partial"].includes(p.status) && <button className="btn btn-ghost btn-sm" onClick={() => openReceive(p)} data-testid={`receive-btn-${p.po_number}`}>Receive</button>}
                    {isOwner && p.status !== "Cancelled" && (p.total - p.amount_paid) > 0.5 && <button className="btn btn-ghost btn-sm" onClick={() => setPaying(p)} data-testid={`pay-btn-${p.po_number}`}>Pay</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {pos.length === 0 && <Empty>No purchases yet</Empty>}
      </div>

      {isOwner && <CreatePO open={showCreate} onClose={() => { setShowCreate(false); setParams({}); }} vendors={vendors} onDone={load} />}
      <ReceivePO po={receiving} onClose={() => setReceiving(null)} onDone={load} />
      <PayVendorModal po={paying} onClose={() => setPaying(null)} onDone={load} />

      <Modal open={!!detail} onClose={() => setDetail(null)} title={detail?.po_number} wide testid="po-detail">
        {detail && (
          <div>
            <div className="grid grid-cols-4 gap-4 mb-5 text-sm">
              <div><div className="eyebrow mb-1">Vendor</div>{detail.vendor?.name}</div>
              <div><div className="eyebrow mb-1">Date</div>{fmtDate(detail.order_date)}</div>
              <div><div className="eyebrow mb-1">Vendor bill</div><span className="font-num">{detail.vendor_bill_no || "—"}</span></div>
              <div><div className="eyebrow mb-1">Status</div><StatusChip status={detail.status} /></div>
            </div>
            <div className="border border-[#E6E1D8] rounded-lg overflow-hidden">
              <table className="tbl">
                <thead><tr><th>Item</th><th className="num">Ordered</th><th className="num">Received</th><th className="num">Unit cost</th><th className="num">Line total</th></tr></thead>
                <tbody>
                  {detail.lines.map((l) => (
                    <tr key={l.sku_id}>
                      <td>{l.style_name} · {l.color}/{l.size}</td>
                      <td className="num font-num">{l.qty_ordered}</td>
                      <td className="num font-num">{l.qty_received}</td>
                      <td className="num font-num">{inr(l.unit_cost)}</td>
                      <td className="num font-num">{inr(l.qty_ordered * l.unit_cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-8 mt-4 text-sm">
              <div className="text-right text-[#5C626A]">Subtotal <span className="font-num ml-3">{inr(detail.subtotal)}</span><br />Tax <span className="font-num ml-3">{inr(detail.tax)}</span><br />Freight <span className="font-num ml-3">{inr(detail.freight)}</span></div>
              <div className="text-right"><div className="eyebrow">Total</div><div className="text-xl font-num font-semibold">{inr(detail.total)}</div>
                <div className="text-xs text-[#8C929B]">Received value {inr(detail.billed)} · Paid {inr(detail.amount_paid)}</div>
                <div className="text-xs font-semibold" style={{ color: detail.payable > 0 ? "#A3411D" : "#2D5A3C" }}>Payable {inr(detail.payable)}</div></div>
            </div>
            {detail.payments?.length > 0 && (
              <div className="mt-5">
                <div className="eyebrow mb-2">Payments to vendor</div>
                <div className="border border-[#E6E1D8] rounded-lg overflow-hidden">
                  <table className="tbl"><tbody>
                    {detail.payments.map((p) => <tr key={p.id}><td>{fmtDate(p.date)}</td><td className="text-[#5C626A]">{p.method}</td><td className="num font-num">{inr(p.amount)}</td></tr>)}
                  </tbody></table>
                </div>
              </div>
            )}
            {isOwner && (
              <div className="flex justify-end gap-2 mt-5">
                {detail.status === "Ordered" && <button className="btn btn-danger btn-sm" onClick={() => cancelPO(detail)}>Cancel order</button>}
                {detail.status !== "Cancelled" && (detail.total - detail.amount_paid) > 0.5 && <button className="btn btn-primary btn-sm" onClick={() => { setPaying(detail); setDetail(null); }}>Pay vendor</button>}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
